import React, { useState, useEffect, useRef } from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useCompany } from '@/contexts/CompanyContext';
import { useTenant } from '@/contexts/TenantContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { logger } from '@/lib/logger';
import CreateTenantModal from './CreateTenantModal';
import WorkspaceAvatar from '@/components/common/WorkspaceAvatar';

export default function CompanySettings({ userRole }) {
    const { currentCompany, updateCompany } = useCompany();
    const { currentTenant, tenants, switchTenant, updateTenant, createTenant } = useTenant();
    const { theme } = useTheme();
    const { user } = useAuth();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState(null);
    const [provisioning2Conf, setProvisioning2Conf] = useState(false);

    // Form state initialized from currentTenant / currentCompany
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        industry: '',
        description: '',
        lightLogoUrl: '',
        darkLogoUrl: ''
    });

    const lightFileInputRef = useRef(null);
    const darkFileInputRef = useRef(null);
    const [uploadingLight, setUploadingLight] = useState(false);
    const [uploadingDark, setUploadingDark] = useState(false);

    // Populate form when tenant/company changes
    useEffect(() => {
        const activeName = currentTenant?.name || currentCompany?.name || '';
        const activeDomain = currentTenant?.domain || currentCompany?.domain || '';
        const activeIndustry = currentTenant?.industry || currentCompany?.industry || '';
        const activeDesc = currentTenant?.description || currentCompany?.description || '';
        const activeLight = currentTenant?.lightLogoUrl || currentCompany?.lightLogoUrl || currentCompany?.logoUrl || '';
        const activeDark = currentTenant?.darkLogoUrl || currentCompany?.darkLogoUrl || currentCompany?.logoUrl || '';

        setFormData({
            name: activeName.replace(' (Fallback)', ''),
            domain: activeDomain,
            industry: activeIndustry,
            description: activeDesc,
            lightLogoUrl: activeLight,
            darkLogoUrl: activeDark
        });
    }, [currentTenant?.id, currentCompany?.id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Helper to upload image or fallback to Base64
    const processLogoUpload = async (file, type) => {
        if (!file) return;
        const isLight = type === 'light';
        if (isLight) setUploadingLight(true);
        else setUploadingDark(true);

        const tenantSlug = currentTenant?.id || 'default';
        try {
            // Attempt Firebase Storage Upload
            const storageRef = ref(storage, `tenants/${tenantSlug}/${type}-logo-${Date.now()}`);
            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);

            setFormData(prev => ({
                ...prev,
                [isLight ? 'lightLogoUrl' : 'darkLogoUrl']: downloadUrl
            }));
            setStatusMsg({ type: 'success', text: `${isLight ? 'Light' : 'Dark'} mode logo uploaded!` });
        } catch (storageErr) {
            logger.warn("Storage upload failed, reading as Base64 data URL fallback:", storageErr);
            // Fallback to Data URL
            const reader = new FileReader();
            reader.onload = (event) => {
                const dataUrl = event.target.result;
                setFormData(prev => ({
                    ...prev,
                    [isLight ? 'lightLogoUrl' : 'darkLogoUrl']: dataUrl
                }));
                setStatusMsg({ type: 'success', text: `${isLight ? 'Light' : 'Dark'} mode logo loaded!` });
            };
            reader.readAsDataURL(file);
        } finally {
            if (isLight) setUploadingLight(false);
            else setUploadingDark(false);
        }
    };

    // 1-Click Provision "The 2.0 Conferences"
    const handleProvisionThe2Conf = async () => {
        setProvisioning2Conf(true);
        setStatusMsg(null);
        try {
            await createTenant({
                name: 'The 2.0 Conferences',
                domain: 'the2conf.com',
                industry: 'Global Conferences & Summits',
                description: 'Flagship enterprise conference platform uniting business, leadership, and tech innovators globally.',
                lightLogoUrl: formData.lightLogoUrl || '',
                darkLogoUrl: formData.darkLogoUrl || ''
            });
            setStatusMsg({ type: 'success', text: 'Workspace "The 2.0 Conferences" provisioned and activated successfully!' });
        } catch (err) {
            logger.error("Failed to provision The 2.0 Conferences:", err);
            setStatusMsg({ type: 'error', text: err.message || 'Failed to provision workspace' });
        } finally {
            setProvisioning2Conf(false);
        }
    };

    // Save All Workspace Changes to Firestore
    const handleSaveWorkspace = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);
        setStatusMsg(null);

        try {
            const updates = {
                name: formData.name.trim(),
                domain: formData.domain.trim(),
                industry: formData.industry.trim(),
                description: formData.description.trim(),
                lightLogoUrl: formData.lightLogoUrl.trim(),
                darkLogoUrl: formData.darkLogoUrl.trim(),
                logoUrl: formData.lightLogoUrl.trim() || formData.darkLogoUrl.trim()
            };

            // 1. Update Tenant in Firestore
            if (currentTenant?.id && updateTenant) {
                await updateTenant(currentTenant.id, updates);
            }

            // 2. Update Company in Firestore
            if (currentCompany?.id && updateCompany) {
                await updateCompany(currentCompany.id, updates);
            }

            setStatusMsg({ type: 'success', text: 'Workspace & branding settings saved successfully to Database!' });
        } catch (err) {
            logger.error("Failed to save workspace settings:", err);
            setStatusMsg({ type: 'error', text: err.message || 'Failed to save settings.' });
        } finally {
            setSaving(false);
        }
    };

    const hasThe2Conf = tenants.some(t => 
        t.name?.toLowerCase().includes('2.0') || 
        t.id?.includes('2-0') || 
        t.domain?.includes('the2conf')
    );

    return (
        <div className="animate-fade-in font-['Lexend_Deca',sans-serif] space-y-6 max-w-[960px]">
            {/* Page Header */}
            <div className="flex items-start justify-between pb-4 border-b border-[var(--border)]">
                <div>
                    <h2 className="text-[20px] font-bold text-[var(--text-primary)] tracking-tight">Workspace & Branding</h2>
                    <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
                        Configure workspace identity, domains, light & dark mode branding logos, and multi-tenant isolation.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {!hasThe2Conf && (
                        <button
                            type="button"
                            onClick={handleProvisionThe2Conf}
                            disabled={provisioning2Conf}
                            className="h-9 px-3.5 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 hover:bg-indigo-500/20 text-[12px] font-semibold flex items-center gap-2 transition-all cursor-pointer"
                        >
                            <ObliqueIcon name="auto_awesome" size={15} />
                            {provisioning2Conf ? 'Provisioning...' : 'Provision "The 2.0 Conferences"'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setIsCreateModalOpen(true)}
                        className="h-9 px-3.5 rounded-xl bg-[var(--bg-muted)] border border-[var(--border)] text-[var(--text-primary)] hover:bg-[var(--bg-surface)] text-[12px] font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                        <ObliqueIcon name="add_business" size={15} />
                        New Workspace
                    </button>
                </div>
            </div>

            {/* Status Message Banner */}
            {statusMsg && (
                <div className={`p-3.5 rounded-xl text-[13px] font-medium flex items-center gap-2.5 transition-all ${
                    statusMsg.type === 'success' 
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' 
                        : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
                }`}>
                    <ObliqueIcon name={statusMsg.type === 'success' ? 'check_circle' : 'error'} size={18} />
                    <span>{statusMsg.text}</span>
                </div>
            )}

            {/* Active Workspace Selector Bar */}
            <div className="p-4 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                        <WorkspaceAvatar 
                            workspace={{
                                ...currentTenant,
                                name: formData.name || currentTenant?.name || currentCompany?.name,
                                lightLogoUrl: formData.lightLogoUrl,
                                darkLogoUrl: formData.darkLogoUrl
                            }} 
                            size="lg" 
                            className="shadow-xs" 
                        />
                        <div>
                            <div className="text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider">
                                Active Workspace
                            </div>
                            <div className="text-[16px] font-bold text-[var(--text-primary)] flex items-center gap-2">
                                {(currentTenant?.name && currentTenant?.name !== 'Primary Workspace' && currentTenant?.name !== 'Primary Organization' ? currentTenant.name.replace(' (Fallback)', '') : currentCompany?.name) || formData.name || 'Workspace'}
                                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[var(--accent-subtle)] text-[var(--accent)]">
                                    ID: {currentTenant?.id || currentCompany?.id || 'default'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Switcher Dropdown & Quick Create */}
                    <div className="flex items-center gap-2.5">
                        {tenants.length > 1 && (
                            <div className="flex items-center gap-2">
                                <span className="text-[12px] text-[var(--text-tertiary)] font-medium">Switch:</span>
                                <select
                                    value={currentTenant?.id || ''}
                                    onChange={(e) => switchTenant(e.target.value)}
                                    className="bg-[var(--bg-muted)] border border-[var(--border)] rounded-xl px-3 py-1.5 text-[12px] font-semibold text-[var(--text-primary)] outline-none cursor-pointer focus:border-[var(--accent)]"
                                >
                                    {tenants.map(t => {
                                        const dName = (t.name && t.name !== 'Primary Workspace' && t.name !== 'Primary Organization') ? t.name : (currentCompany?.name || 'The Scale Agency');
                                        return (
                                            <option key={t.id} value={t.id}>
                                                {dName} ({t.id})
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setIsCreateModalOpen(true)}
                            className="h-8 px-3 rounded-lg bg-[var(--accent)] hover:opacity-90 text-white text-[12px] font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs border-0"
                        >
                            <ObliqueIcon name="add" size={14} />
                            <span>Create Workspace</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Settings Form */}
            <form onSubmit={handleSaveWorkspace} className="space-y-6">
                {/* General Information Card */}
                <div className="p-6 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] shadow-sm space-y-5">
                    <div className="flex items-center gap-2 pb-3 border-b border-[var(--border)]">
                        <ObliqueIcon name="badge" size={18} className="text-[var(--accent)]" />
                        <h3 className="text-[14px] font-bold text-[var(--text-primary)]">Workspace Details</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">
                                Workspace / Tenant Name <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g. The 2.0 Conferences"
                                required
                                className="w-full h-10 px-3.5 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-all focus:border-[var(--accent)] focus:bg-[var(--bg-surface)]"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">
                                Primary Domain / URL
                            </label>
                            <input
                                type="text"
                                name="domain"
                                value={formData.domain}
                                onChange={handleInputChange}
                                placeholder="e.g. the2conf.com"
                                className="w-full h-10 px-3.5 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-all focus:border-[var(--accent)] focus:bg-[var(--bg-surface)]"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">
                                Industry / Sector
                            </label>
                            <input
                                type="text"
                                name="industry"
                                value={formData.industry}
                                onChange={handleInputChange}
                                placeholder="e.g. Global Conferences, SaaS, Marketing"
                                className="w-full h-10 px-3.5 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-all focus:border-[var(--accent)] focus:bg-[var(--bg-surface)]"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">
                                Workspace Description
                            </label>
                            <input
                                type="text"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Brief overview of this organization..."
                                className="w-full h-10 px-3.5 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-all focus:border-[var(--accent)] focus:bg-[var(--bg-surface)]"
                            />
                        </div>
                    </div>
                </div>

                {/* Dual-Theme Branding Card (Light & Dark Logos) */}
                <div className="p-6 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] shadow-sm space-y-5">
                    <div className="flex items-center justify-between pb-3 border-b border-[var(--border)]">
                        <div className="flex items-center gap-2">
                            <ObliqueIcon name="palette" size={18} className="text-[var(--accent)]" />
                            <div>
                                <h3 className="text-[14px] font-bold text-[var(--text-primary)]">Dual-Theme Branding</h3>
                                <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                                    Set separate logos for Light Mode and Dark Mode so your brand always looks crisp.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Light Mode Logo */}
                        <div className="p-4 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)]/50 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-[12px] font-bold text-[var(--text-primary)]">
                                    <ObliqueIcon name="light_mode" size={15} className="text-amber-500" />
                                    <span>Light Mode Logo</span>
                                </div>
                                <span className="text-[10px] font-medium text-[var(--text-tertiary)]">Shown in Light theme</span>
                            </div>

                            {/* Light Mode Preview Canvas */}
                            <div className="h-20 w-full rounded-xl bg-white border border-slate-200 flex items-center justify-center p-3 shadow-inner overflow-hidden">
                                {formData.lightLogoUrl ? (
                                    <img
                                        src={formData.lightLogoUrl}
                                        alt="Light Mode Logo Preview"
                                        className="max-h-full max-w-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <div className="flex items-center gap-1.5 text-slate-400 text-[11px] font-medium">
                                        <ObliqueIcon name="image" size={16} />
                                        <span>No light logo configured</span>
                                    </div>
                                )}
                            </div>

                            {/* Input + Upload */}
                            <div className="space-y-2">
                                <input
                                    type="text"
                                    name="lightLogoUrl"
                                    value={formData.lightLogoUrl}
                                    onChange={handleInputChange}
                                    placeholder="Paste Light Logo image URL..."
                                    className="w-full h-9 px-3 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[12px] outline-none focus:border-[var(--accent)]"
                                />
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        ref={lightFileInputRef}
                                        accept="image/svg+xml,image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={(e) => e.target.files?.[0] && processLogoUpload(e.target.files[0], 'light')}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => lightFileInputRef.current?.click()}
                                        disabled={uploadingLight}
                                        className="flex-1 h-8 rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] hover:bg-[var(--bg-muted)] text-[11px] font-semibold text-[var(--text-secondary)] flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                                    >
                                        <ObliqueIcon name="upload" size={13} />
                                        {uploadingLight ? 'Uploading...' : 'Upload Image File'}
                                    </button>
                                    {formData.lightLogoUrl && (
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, lightLogoUrl: '' }))}
                                            className="h-8 px-2.5 rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] text-rose-500 hover:bg-rose-500/10 text-[11px] font-semibold cursor-pointer"
                                            title="Clear"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Dark Mode Logo */}
                        <div className="p-4 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)]/50 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-[12px] font-bold text-[var(--text-primary)]">
                                    <ObliqueIcon name="dark_mode" size={15} className="text-indigo-400" />
                                    <span>Dark Mode Logo</span>
                                </div>
                                <span className="text-[10px] font-medium text-[var(--text-tertiary)]">Shown in Dark theme</span>
                            </div>

                            {/* Dark Mode Preview Canvas */}
                            <div className="h-20 w-full rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center p-3 shadow-inner overflow-hidden">
                                {formData.darkLogoUrl ? (
                                    <img
                                        src={formData.darkLogoUrl}
                                        alt="Dark Mode Logo Preview"
                                        className="max-h-full max-w-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <div className="flex items-center gap-1.5 text-slate-600 text-[11px] font-medium">
                                        <ObliqueIcon name="image" size={16} />
                                        <span>No dark logo configured</span>
                                    </div>
                                )}
                            </div>

                            {/* Input + Upload */}
                            <div className="space-y-2">
                                <input
                                    type="text"
                                    name="darkLogoUrl"
                                    value={formData.darkLogoUrl}
                                    onChange={handleInputChange}
                                    placeholder="Paste Dark Logo image URL..."
                                    className="w-full h-9 px-3 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[12px] outline-none focus:border-[var(--accent)]"
                                />
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        ref={darkFileInputRef}
                                        accept="image/svg+xml,image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={(e) => e.target.files?.[0] && processLogoUpload(e.target.files[0], 'dark')}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => darkFileInputRef.current?.click()}
                                        disabled={uploadingDark}
                                        className="flex-1 h-8 rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] hover:bg-[var(--bg-muted)] text-[11px] font-semibold text-[var(--text-secondary)] flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                                    >
                                        <ObliqueIcon name="upload" size={13} />
                                        {uploadingDark ? 'Uploading...' : 'Upload Image File'}
                                    </button>
                                    {formData.darkLogoUrl && (
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, darkLogoUrl: '' }))}
                                            className="h-8 px-2.5 rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] text-rose-500 hover:bg-rose-500/10 text-[11px] font-semibold cursor-pointer"
                                            title="Clear"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save Button Action */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
                    <button
                        type="submit"
                        disabled={saving}
                        className="h-10 px-6 rounded-xl bg-[var(--accent)] hover:opacity-95 text-white font-bold text-[13px] shadow-md flex items-center gap-2 transition-all cursor-pointer disabled:opacity-50"
                    >
                        <ObliqueIcon name={saving ? 'sync' : 'save'} size={16} className={saving ? 'animate-spin' : ''} />
                        {saving ? 'Saving to Database...' : 'Save Workspace Settings'}
                    </button>
                </div>
            </form>

            {/* Create Tenant Modal */}
            <CreateTenantModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
            />
        </div>
    );
}
